/* emailTransport — jedno místo, které ví, KUDY poradce dnes pošle e-mail.
 *
 * Proč to existuje: všechna odesílací tlačítka v aplikaci mířila natvrdo na
 * /api/gmail/send. Když správce nemá u Googlu doplněné přístupy, ten endpoint
 * vrací 503 — a poradce, který si poctivě připojil firemní e-mail přes SMTP
 * a viděl zelené „Firemní e-mail propojen", stejně nemohl odeslat nic.
 * Endpoint /api/email/smtp/send přitom funguje, jen ho nic nevolalo.
 *
 * Použití:
 *   const t = await nactiEmailTransport();          // { transport, email, … }
 *   await odesliEmail({ to, subject, bodyText }, t);
 *
 * Preference: Gmail (když je připojený) → jinak SMTP → jinak nic.
 */
import api from "@/lib/api";

/** FastAPI vrací u 422 `detail` jako POLE objektů.
 *
 * Vykreslit ho rovnou do JSX znamenalo „Objects are not valid as a React child"
 * → spadla CELÁ aplikace a poradci naskočila červená obrazovka. Reprodukováno
 * živě překlepem v e-mailu (jan@beplan). Tahle funkce je pojistka: ať přijde
 * cokoli, vrátí se string.
 */
export function textChyby(detail, nahradni = "") {
  if (detail == null) return nahradni;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const vety = detail
      .map((d) => (typeof d === "string" ? d : d?.msg || d?.detail || ""))
      .filter(Boolean);
    return vety.length ? vety.join(" ") : nahradni;
  }
  if (typeof detail === "object") {
    return detail.message || detail.msg || detail.detail || nahradni;
  }
  return String(detail);
}

/** Chyba z axiosu → jedna česká věta, nikdy objekt. */
export function zpravaChyby(err, nahradni = "Odeslání selhalo") {
  return textChyby(err?.response?.data?.detail, err?.message || nahradni);
}

/** Prostý text → jednoduché HTML (escapované). */
export function textNaHtml(text) {
  const esc = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc
    .split(/\n{2,}/)
    .map((odstavec) => `<p>${odstavec.replace(/\n/g, "<br />")}</p>`)
    .join("\n");
}

/** Zjistí obě cesty najednou. Nikdy nevyhodí výjimku. */
export async function nactiEmailTransport() {
  const [g, s] = await Promise.allSettled([
    api.get("/api/oauth/gmail/status"),
    api.get("/api/email/smtp/status"),
  ]);
  const gmail = g.status === "fulfilled" ? (g.value?.data || {}) : null;
  const smtp = s.status === "fulfilled" ? (s.value?.data || {}) : null;

  const gmailPripojen = !!gmail?.connected;
  const smtpPripojen = !!smtp?.connected;
  const gmailNakonfigurovan = gmail ? gmail.configured !== false : true;

  let transport = null;
  if (gmailPripojen) transport = "gmail";
  else if (smtpPripojen) transport = "smtp";

  let duvod = null;
  if (!transport) duvod = gmailNakonfigurovan ? "nic_nepripojeno" : "google_nedostupny";

  return {
    transport,
    email: transport === "gmail" ? (gmail?.email || "") : (smtp?.email || ""),
    gmail: gmail || { connected: false },
    smtp: smtp || { connected: false },
    gmailNakonfigurovan,
    // true = ani jeden status se nepodařilo zjistit; netvrdíme „nepřipojeno"
    neznamy: gmail === null && smtp === null,
    duvod,
  };
}

/** Odešle e-mail tou cestou, která je k dispozici.
 *
 * `stav` je výsledek nactiEmailTransport(); když se nepředá, dotáhne se sám.
 * Vrací { transport, saved_to_sent? }.
 */
export async function odesliEmail(zprava, stav = null) {
  const s = stav || (await nactiEmailTransport());
  const prijemci = Array.isArray(zprava.to) ? zprava.to : [zprava.to].filter(Boolean);
  const text = zprava.bodyText || "";
  const html = zprava.bodyHtml || (text ? textNaHtml(text) : "");

  if (s.transport === "gmail") {
    await api.post("/api/gmail/send", {
      to: prijemci,
      subject: zprava.subject,
      body_text: text || undefined,
      body_html: zprava.bodyHtml || undefined,
      contact_id: zprava.contactId || null,
    });
    return { transport: "gmail" };
  }

  if (s.transport === "smtp") {
    const { data } = await api.post("/api/email/smtp/send", {
      to: prijemci,
      subject: zprava.subject,
      body_html: html,
      body_text: text,
      cc: zprava.cc || [],
      bcc: zprava.bcc || [],
      contact_id: zprava.contactId || null,
    });
    return { transport: "smtp", saved_to_sent: !!data?.saved_to_sent };
  }

  const e = new Error(
    s.gmailNakonfigurovan
      ? "E-mail zatím není propojený. Připoj si ho v Nastavení — firemní e-mail (SMTP) funguje hned."
      : "E-mail zatím není propojený. Gmail správce ještě nezapnul, ale firemní e-mail (SMTP) v Nastavení funguje hned."
  );
  e.bezTransportu = true;
  throw e;
}
